import * as React from 'react';
import { Link, useLocation } from 'react-router';
import { Menu, X, ChevronDown } from 'lucide-react';
import { Button, cn } from '@afrilink/ui';
import { navigationConfig, isNavGroup, type NavGroup, type NavLeaf } from '../config/navigation';
import { getPortalUrl } from '../config/portal';
import { marketingCta, marketingCtaText } from '../components/buttonStyles';

/**
 * Header — Switchboard-Modernist redesign.
 *
 * Logotype in Bricolage display + small mono "HOSTING" tagline; nav items
 * carry an animated primary-bar active indicator (in/out on hover) instead
 * of relying on text colour alone; CTA pair (Login / Get Started) shares the
 * marketing button vocabulary (uppercase + tracked + sharp corners).
 *
 * Scroll behaviour: above-fold = transparent, scrolled = bg blur with a
 * primary-tinted hairline rule. Mobile menu uses the same vocabulary.
 */
export function Header() {
  const { pathname } = useLocation();
  const [scrolled, setScrolled] = React.useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);

  React.useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  React.useEffect(() => {
    setMobileMenuOpen(false);
  }, [pathname]);

  React.useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [mobileMenuOpen]);

  return (
    <>
      <header
        className={cn(
          'fixed inset-x-0 top-0 z-50 transition-all duration-300',
          scrolled
            ? 'border-b border-white/10 bg-neutral-950/85 py-2 shadow-[0_8px_32px_-12px_rgba(0,0,0,0.6)] backdrop-blur-md'
            : 'border-b border-transparent bg-transparent py-3',
        )}
      >
        {/* Hairline primary accent on the bottom edge — barely visible until scrolled */}
        {scrolled && (
          <div
            aria-hidden="true"
            className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-primary/40 to-transparent"
          />
        )}

        <nav className="container mx-auto flex items-center justify-between px-4">
          <Link to="/" className="flex items-center gap-3">
            <img
              src="/images/logo.png"
              alt="SightHost"
              width={160}
              height={48}
              className="h-9 w-auto brightness-0 invert"
              loading="eager"
              decoding="async"
            />
            {/* Brand-text block dropped 2026-07-01 per owner: the logo
                already carries the "SightHost" wordmark; the adjacent
                "SightHost" span duplicated it. Designer brief §7 had
                already greenlit "drop entirely" as an option. */}
          </Link>

          <div
            className="hidden items-center gap-1 md:flex"
            role="navigation"
            aria-label="Main navigation"
          >
            {navigationConfig.mainNav.map((item) => {
              if (isNavGroup(item)) {
                return <NavGroupItem key={item.title} group={item} pathname={pathname} />;
              }
              const isActive = pathname === item.href;
              return (
                <Link
                  key={item.href}
                  to={item.href}
                  aria-current={isActive ? 'page' : undefined}
                  className={cn(
                    'group relative px-4 py-2 text-[13px] font-medium tracking-wide transition-colors duration-200',
                    isActive ? 'text-white' : 'text-white/60 hover:text-white',
                  )}
                >
                  {item.title}
                  {/* Active / hover indicator — primary bar grows from centre */}
                  <span
                    aria-hidden="true"
                    className={cn(
                      'pointer-events-none absolute inset-x-3 -bottom-px h-[2px] origin-center bg-primary transition-transform duration-300 ease-out',
                      isActive ? 'scale-x-100' : 'scale-x-0 group-hover:scale-x-50',
                    )}
                  />
                </Link>
              );
            })}
          </div>

          <div className="hidden items-center gap-2 md:flex">
            <a
              href={getPortalUrl(undefined, { medium: 'header', campaign: 'general' })}
              rel="noopener"
            >
              <Button
                size="sm"
                className={cn(marketingCta.ghost, marketingCtaText.sm)}
              >
                Sign In
              </Button>
            </a>
            <Link to="/hosting">
              <Button
                size="sm"
                className={cn(marketingCta.primary, marketingCtaText.sm)}
              >
                Get Started
              </Button>
            </Link>
          </div>

          <button
            className="rounded-sm p-2 text-white/70 transition-colors hover:bg-white/10 hover:text-white md:hidden"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={mobileMenuOpen}
            aria-controls="mobile-menu"
          >
            {mobileMenuOpen ? (
              <X className="h-5 w-5" aria-hidden="true" />
            ) : (
              <Menu className="h-5 w-5" aria-hidden="true" />
            )}
          </button>
        </nav>
      </header>

      <div
        id="mobile-menu"
        className={cn(
          'fixed inset-0 z-40 transition-opacity duration-300 md:hidden',
          mobileMenuOpen
            ? 'pointer-events-auto opacity-100'
            : 'pointer-events-none opacity-0',
        )}
        aria-hidden={!mobileMenuOpen}
      >
        <div
          className="absolute inset-0 bg-neutral-950/85 backdrop-blur-sm"
          onClick={() => setMobileMenuOpen(false)}
        />

        <div
          className={cn(
            'absolute right-0 top-0 h-full w-80 border-l border-white/10 bg-neutral-950 shadow-2xl transition-transform duration-300 ease-out',
            mobileMenuOpen ? 'translate-x-0' : 'translate-x-full',
          )}
        >
          {/* Mobile drawer top edge — primary hairline echoes the desktop active indicator */}
          <div
            aria-hidden="true"
            className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-primary/50 to-transparent"
          />

          <div className="flex h-full flex-col">
            <div className="flex items-center justify-between border-b border-white/10 p-5">
              <div className="flex items-center gap-2.5">
                <img
                  src="/images/logo.png"
                  alt="SightHost"
                  width={120}
                  height={36}
                  className="h-7 w-auto brightness-0 invert"
                  loading="eager"
                  decoding="async"
                />
                <div className="flex flex-col leading-tight">
                  <span className="font-display text-sm font-bold text-white">SightHost</span>
                  <span className="font-mono text-[8px] uppercase tracking-[0.3em] text-white/45">
                    Hosting
                  </span>
                </div>
              </div>
              <button
                onClick={() => setMobileMenuOpen(false)}
                className="rounded-sm p-2 text-white/60 hover:bg-white/10 hover:text-white"
                aria-label="Close menu"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Eyebrow */}
            <p className="px-5 pt-5 font-mono text-[10px] uppercase tracking-[0.3em] text-white/35">
              Sections
            </p>

            <nav
              className="flex-1 overflow-y-auto p-5 pt-2"
              aria-label="Mobile navigation"
            >
              <div className="space-y-0.5">
                {/* Flatten nav groups so each leaf is a sequentially-numbered
                 *  drawer entry — keeps the "01 / 02 / 03 …" rhythm consistent
                 *  whether or not the desktop nav uses dropdowns. */}
                {navigationConfig.mainNav
                  .flatMap((item) => (isNavGroup(item) ? item.children : [item]))
                  .map((link, index) => {
                  const isActive = pathname === link.href;
                  return (
                    <Link
                      key={link.href}
                      to={link.href}
                      className={cn(
                        'group flex items-center gap-4 rounded-sm px-3 py-3 text-base font-medium transition-colors',
                        isActive
                          ? 'bg-white/[0.06] text-white'
                          : 'text-white/70 hover:bg-white/[0.04] hover:text-white',
                      )}
                      onClick={() => setMobileMenuOpen(false)}
                      aria-current={isActive ? 'page' : undefined}
                    >
                      <span
                        className={cn(
                          'font-mono text-[10px] uppercase tracking-widest',
                          isActive ? 'text-primary' : 'text-white/30',
                        )}
                      >
                        {String(index + 1).padStart(2, '0')}
                      </span>
                      <span className="flex-1">{link.title}</span>
                      {isActive && (
                        <span
                          aria-hidden="true"
                          className="h-2 w-2 rounded-full bg-primary shadow-[0_0_8px_rgba(246,150,12,0.7)]"
                        />
                      )}
                    </Link>
                  );
                })}
              </div>
            </nav>

            <div className="space-y-3 border-t border-white/10 p-5">
              <a
                href={getPortalUrl(undefined, { medium: 'header', campaign: 'general' })}
                rel="noopener"
                className="block"
              >
                <Button
                  size="default"
                  className={cn(marketingCta.ghost, marketingCtaText.default, 'w-full')}
                >
                  Sign In
                </Button>
              </a>
              <Link to="/hosting" className="block">
                <Button
                  size="default"
                  className={cn(marketingCta.primary, marketingCtaText.default, 'w-full')}
                >
                  Get Started
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

/**
 * NavGroupItem — desktop dropdown trigger for grouped nav items.
 *
 * Renders the same animated-bar treatment as flat items, with a chevron and
 * a click-to-reveal panel of children. The trigger is "active" (full-width
 * bar) whenever any child route matches the current pathname.
 *
 * Click-outside + Escape close the panel. Selecting a child closes too.
 */
function NavGroupItem({ group, pathname }: { group: NavGroup; pathname: string }) {
  const [isOpen, setIsOpen] = React.useState(false);
  const wrapperRef = React.useRef<HTMLDivElement>(null);
  const isActive = group.children.some((c) => pathname === c.href);

  // Auto-close on route change (e.g. browser back-button after a child select).
  React.useEffect(() => {
    setIsOpen(false);
  }, [pathname]);

  React.useEffect(() => {
    if (!isOpen) return;
    function handleClick(e: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    }
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setIsOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [isOpen]);

  return (
    <div ref={wrapperRef} className="relative">
      <button
        type="button"
        onClick={() => setIsOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        className={cn(
          'group relative inline-flex items-center gap-1 px-4 py-2 text-[13px] font-medium tracking-wide transition-colors duration-200 focus-visible:outline-none focus-visible:text-white',
          isActive ? 'text-white' : 'text-white/60 hover:text-white',
        )}
      >
        {group.title}
        <ChevronDown
          aria-hidden="true"
          className={cn(
            'h-3 w-3 transition-transform duration-200',
            isOpen && 'rotate-180',
          )}
        />
        <span
          aria-hidden="true"
          className={cn(
            'pointer-events-none absolute inset-x-3 -bottom-px h-[2px] origin-center bg-primary transition-transform duration-300 ease-out',
            isActive || isOpen ? 'scale-x-100' : 'scale-x-0 group-hover:scale-x-50',
          )}
        />
      </button>

      {isOpen && (
        <div
          role="menu"
          aria-label={group.title}
          className="absolute left-1/2 top-full z-50 mt-2 w-56 -translate-x-1/2"
        >
          <div className="relative rounded-sm border border-white/10 bg-neutral-950/95 p-2 shadow-2xl backdrop-blur-md">
            {/* Hairline accent on the top edge — echoes the scrolled header rule */}
            <div
              aria-hidden="true"
              className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-primary/50 to-transparent"
            />
            {group.children.map((child: NavLeaf, i: number) => {
              const childActive = pathname === child.href;
              return (
                <Link
                  key={child.href}
                  to={child.href}
                  role="menuitem"
                  onClick={() => setIsOpen(false)}
                  aria-current={childActive ? 'page' : undefined}
                  className={cn(
                    'flex items-center gap-3 rounded-sm px-3 py-2 text-sm transition-colors',
                    childActive
                      ? 'bg-white/[0.06] text-white'
                      : 'text-white/70 hover:bg-white/[0.04] hover:text-white',
                  )}
                >
                  <span
                    className={cn(
                      'font-mono text-[10px] uppercase tracking-widest',
                      childActive ? 'text-primary' : 'text-white/30',
                    )}
                  >
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <span className="flex-1">{child.title}</span>
                </Link>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
